import { Component, Inject, Vue } from "vue-property-decorator";
import Utils from "@/vue/infrastructure/utils/helpers";
import vuescroll from "vuescroll";
import DfStore from "@/vue/domain/store/df-store";
import { CONTAINER_TYPES } from "@/vue/infrastructure/ioc/container-types";
import ServiceInterface from "@/vue/service/service-interface";
import DfProperty from "@/vue/domain/content/df-property";
import Swal from "sweetalert2";
import DfBrand from "@/vue/domain/brand/df-brand";
import DfUser from "@/vue/domain/user/df-user";

@Component({
  components: {
    DfHeaderMobile: () => Utils.externalComponent2("df-header-mobile"),
    DfFormManager: () => Utils.externalComponent2("df-form-manager"),
    DfStoreCard: () => Utils.externalComponent2("df-store-card"),
    DfErrorCard: () => import(/* webpackChunkName: 'df-error-card' */ "@/vue/components/df-page-error/df-error-card/df-error-card.vue"),
    vuescroll,
  },
})
export default class DfPageUserRegistrationComponent extends Vue {
  @Inject(CONTAINER_TYPES.ServiceInterface)
  private service!: ServiceInterface;

  private registrationData: any = {};
  private cardCheckDone = false;
  private cardRegistrationChoice = false;

  get brand(): DfBrand {
    return this.$store.getters.brand;
  }

  get cardCheckFields(): any {
    const camiCardNumber: DfProperty = new DfProperty("STRING", "cardNumber", "Codice tessera", [{ string: null }], true);
    const firstName: DfProperty = new DfProperty("STRING", "firstName", "Nome", [{ string: null }], true);
    const lastName: DfProperty = new DfProperty("STRING", "lastName", "Cognome", [{ string: null }], true);
    const taxCode: DfProperty = new DfProperty("STRING", "taxCode", "Codice fiscale", [{ string: null }], true);
    const dateOfBirth: DfProperty = new DfProperty("DATE", "birthDate", "Data di nascita", [{ date: null }], true);

    if (this.currentUser) {
      return { properties: [camiCardNumber] };
    } else {
      switch (this.registrationMode) {
        case Utils.REGISTRATION_MODE_SINGLE_CHECK:
          return { properties: [camiCardNumber, taxCode] };
        case Utils.REGISTRATION_MODE_FULL_CHECK:
          return { properties: [camiCardNumber, firstName, lastName, taxCode, dateOfBirth] };
        default:
          return { properties: [] };
      }
    }
  }

  get registrationDataFields(): any {
    const camiCardNumber: DfProperty = new DfProperty("STRING", "cardNumber", "Codice tessera", [{ string: null }], true);
    const firstName: DfProperty = new DfProperty("STRING", "firstName", "Nome", [{ string: null }], true);
    const lastName: DfProperty = new DfProperty("STRING", "lastName", "Cognome", [{ string: null }], true);
    const taxCode: DfProperty = new DfProperty("STRING", "taxCode", "Codice fiscale", [{ string: null }], true);
    const dateOfBirth: DfProperty = new DfProperty("DATE", "birthDate", "Data di nascita", [{ date: null }], true);

    if (this.currentUser) {
      return { properties: [camiCardNumber] };
    } else {
      switch (this.registrationMode) {
        case Utils.REGISTRATION_MODE_SINGLE_CHECK:
          return { properties: [camiCardNumber, taxCode] };
        case Utils.REGISTRATION_MODE_FULL_CHECK:
          return { properties: [camiCardNumber, firstName, lastName, taxCode, dateOfBirth] };
        default:
          return { properties: [] };
      }
    }
  }

  get currentStore(): DfStore {
    return this.$store.getters.currentStore;
  }

  get currentUser(): DfUser {
    return this.$store.getters.user;
  }

  get showCardCheck(): boolean {
    return this.registrationMode === Utils.REGISTRATION_MODE_SINGLE_CHECK || this.registrationMode === Utils.REGISTRATION_MODE_FULL_CHECK;
  }

  get registrationMode(): string {
    const propertyValue: any = Utils.getPropertyValue(this.brand, Utils.PROPERTY_REGISTRATION_MODE, "LIST");
    return propertyValue ? propertyValue.key : Utils.REGISTRATION_MODE_STANDARD;
  }

  beforeMount() {
    if (Utils.enablePrivateArea()) {
      this.getRegistrationData();
      if (this.currentUser) {
        this.cardRegistrationChoice = true;
      }
    } else {
      this.$router.push({ name: "home-store", params: { storeAlias: this.currentStore.alias } });
    }
  }

  private saveCamiCard(callbackFunctions: any) {
    this.currentUser ? this.bindCamiCard(callbackFunctions) : this.checkCamiCard(callbackFunctions);
  }

  private bindCamiCard(callbackFunctions: any) {
    const params: any = {};
    this.cardCheckFields.properties.forEach((property: DfProperty) => {
      params[property.code] = property.values[0][property.type.toLowerCase()];
    });

    this.service
      .bindCamiCard(params)
      .then((response: any) => {
        Swal.fire({
          title: this.$t("df-page-user-registration.modal.bind-cami-card.save.title").toString(),
          text: this.$t("df-page-user-registration.modal.bind-cami-card.save.body").toString(),
          icon: "info",
          confirmButtonColor: "#3085d6",
          confirmButtonText: this.$t("df-page-user-registration.modal.bind-cami-card.save.ok").toString(),
        }).then((result) => {
          this.$router.push({ name: "account-card" });
        });
      })
      .catch((error: any) => {
        if (callbackFunctions.catch) {
          callbackFunctions.catch(error);
        }
        this.cardCheckDone = false;
      });
  }

  private checkCamiCard(callbackFunctions: any) {
    const params: any = {};
    this.cardCheckFields.properties.forEach((property: DfProperty) => {
      params[property.code] = property.values[0][property.type.toLowerCase()];
      if (property.code === "taxCode") {
        params[property.code] = (params[property.code] || "").toUpperCase();
      }
    });

    this.service
      .checkCamiCard(params)
      .then((response: any) => {
        Swal.fire({
          title: this.$t("df-page-user-registration.modal.check-cami-card.save.title").toString(),
          text: this.$t("df-page-user-registration.modal.check-cami-card.save.body").toString(),
          icon: "info",
          confirmButtonColor: "#3085d6",
          confirmButtonText: this.$t("df-page-user-registration.modal.check-cami-card.save.ok").toString(),
        }).then((result) => {
          if (callbackFunctions.then) {
            callbackFunctions.then(response);
          }

          this.registrationData.properties.forEach((property: DfProperty) => {
            switch (property.code) {
              case "CAMI_CARD_NUMBER":
                property.readOnly = !!params.cardNumber;
                property.values = [{ string: params.cardNumber }];
                break;
              case "TAX_CODE":
                property.readOnly = !!params.taxCode;
                property.values = [{ string: (params.taxCode || "").toUpperCase() }];
                break;
              case "FIRST_NAME":
                property.readOnly = !!params.firstName;
                property.values = [{ string: params.firstName }];
                break;
              case "LAST_NAME":
                property.readOnly = !!params.lastName;
                property.values = [{ string: params.lastName }];
                break;
              case "DATE_OF_BIRTH":
                property.readOnly = !!params.birthDate;
                property.values = [{ date: params.birthDate }];
                break;
            }
          });
          this.cardCheckDone = true;
        });
      })
      .catch((error: any) => {
        if (callbackFunctions.catch) {
          callbackFunctions.catch(error);
        }
        this.cardCheckDone = false;
      });
  }

  private goToStorePage(store: DfStore) {
    this.$router.push({ name: "store", params: { storeAlias: store.alias } }).catch((err) => err);
  }

  private openMap() {
    window.open(`${Utils.GOOGLE_MAP_URL}${Utils.storeGoogleMapsCoordinates(this.currentStore)}`, "_blank");
  }

  private getRegistrationData() {
    this.service
      .getRegistrationData()
      .then((registrationData: any) => {
        this.registrationData = registrationData;
      })
      .catch((error: Error) => {
        Utils.defaultApiErrorHandler(error, this, () => {
          this.getRegistrationData();
        });
      });
  }

  private saveRegistrationData(callbackFunctions: any) {
    const params: any = {
      confirmPassword: this.registrationData["confirmPassword"],
      email: this.registrationData["email"],
      password: this.registrationData["password"],
      privacyConsent: this.registrationData["privacyConsent"],
      clubCardConsent: this.registrationData["clubCardConsent"],
      storeIdentifier: this.currentStore.identifier,
      properties: (this.registrationData.properties || []).map((property: DfProperty) => {
        return {
          type: property.type,
          code: property.code,
          values: property.values.map((value: any) => {
            if (property.type === "LIST") {
              const listValue = value.list ? value.list.key : null;
              return listValue && listValue !== Utils.FAKE_VALUE ? { list: listValue } : { list: null };
            } else if (property.code === "TAX_CODE" && value.string) {
              value.string = value.string.toUpperCase();
              return value;
            } else {
              return value;
            }
          }),
        };
      }),
    };
    this.service
      .setRegistrationData(params)
      .then((response: any) => {
        if (callbackFunctions.then) {
          callbackFunctions.then(response);
        }
        Utils.setAnaliticsTraker({
          event: "user_registration",
          origin: "smt-digitalflyer-ui",
          email: this.registrationData["email"],
        });
        Swal.fire({
          title: this.$t("df-page-user-registration.modal.save.title").toString(),
          text: this.$t("df-page-user-registration.modal.save.body").toString(),
          icon: "info",
          confirmButtonColor: "#3085d6",
          confirmButtonText: this.$t("df-page-user-registration.modal.save.ok").toString(),
        }).then((result) => {
          if (result.value) {
            this.$router.push({ name: "home-store", params: { storeAlias: this.currentStore.alias } }).then(() => {
              this.$store.dispatch("setShowLogin", true);
            });
          }
        });
      })
      .catch((error: any) => {
        if (callbackFunctions.catch) {
          callbackFunctions.catch(error);
        }
      });
  }

  private setCardRegistrationChoice(useCard: boolean) {
    this.cardRegistrationChoice = true;
    this.cardCheckDone = !useCard;
    this.registrationData.properties = this.registrationData.properties.filter((property: DfProperty) => property.code !== "CAMI_CARD_NUMBER" || useCard);
  }
}
